/** Escolher a nova senha (SPEC-IDN-012).
 *
 * A validade do link só é conhecida no envio: não existe rota para consultá-lo
 * antes, e criar uma daria a quem tem um token qualquer uma forma barata de
 * testá-lo. A tela aceita a senha primeiro e trata a recusa depois — custa um
 * campo preenchido à toa no caso raro do link vencido, e economiza uma rota
 * que serviria melhor a quem ataca do que a quem esqueceu a senha.
 *
 * Concluir **revoga todas as sessões**, inclusive as legítimas em outros
 * aparelhos. A tela avisa isso antes, não depois.
 */

import { useState, type SyntheticEvent } from "react";

import { ApiError } from "@/lib/http";
import { Botao } from "@/shared/ui/Botao";
import { Campo } from "@/shared/ui/Campo";

import { concluirRedefinicao } from "./api";

const SENHA_MINIMA = 12;

export function NovaSenha({ token, aoConcluir }: { token: string; aoConcluir: () => void }) {
  const [senha, setSenha] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  async function enviar(evento: SyntheticEvent) {
    evento.preventDefault();
    if (senha.length < SENHA_MINIMA || enviando) return;

    setEnviando(true);
    setErro(null);
    try {
      await concluirRedefinicao(token, senha);
      setPronto(true);
    } catch (falha) {
      setErro(
        falha instanceof ApiError
          ? falha.message
          : "Não foi possível concluir. Tente de novo em instantes.",
      );
    } finally {
      setEnviando(false);
    }
  }

  if (pronto) {
    return (
      <Moldura>
        <h1 className="font-display text-display-md text-texto">Senha alterada</h1>
        <p className="font-texto text-body text-texto-suave">
          Sua nova senha já vale. Por segurança, encerramos as sessões abertas em outros aparelhos —
          entre de novo onde precisar.
        </p>
        <Botao larguraTotal onClick={aoConcluir}>
          Entrar
        </Botao>
      </Moldura>
    );
  }

  return (
    <Moldura>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-display-md text-texto">Escolha uma nova senha</h1>
        <p className="font-texto text-body text-texto-suave">
          Ao concluir, as sessões abertas em outros aparelhos serão encerradas.
        </p>
      </div>

      <form
        onSubmit={(evento) => void enviar(evento)}
        aria-label="Definir nova senha"
        className="flex flex-col gap-4"
      >
        <Campo
          rotulo="Nova senha"
          type="password"
          value={senha}
          onChange={(evento) => {
            setSenha(evento.target.value);
          }}
          autoComplete="new-password"
          ajuda={`Ao menos ${SENHA_MINIMA} caracteres.`}
          required
          autoFocus
        />

        <Botao
          type="submit"
          larguraTotal
          carregando={enviando}
          disabled={senha.length < SENHA_MINIMA}
        >
          Salvar nova senha
        </Botao>

        {erro !== null && (
          <p role="alert" className="font-texto text-caption text-carmim">
            {erro}
          </p>
        )}
      </form>
    </Moldura>
  );
}

function Moldura({ children }: { children: React.ReactNode }) {
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas px-6">
      <div className="flex w-full max-w-[420px] flex-col gap-6">{children}</div>
    </main>
  );
}
